import { useCallback, useEffect, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import GlobalLoader from 'components/elements/GlobalLoader';
import { SidebarLayout } from 'components/layout/SidebarLayout';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import SearchResultsViews from 'components/widgets/ProjectInsights/SearchResultsViews';
import ProjectInsightsToolbar from 'components/widgets/ProjectInsights/Toolbar';
import { isChatCreatedSavedSearch } from 'components/widgets/ProjectInsights/Toolbar/getSavedSearchEditorType';
import {
  CHAT_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
} from 'components/widgets/ProjectInsights/Toolbar/sidebarWidth';
import { useSelectedGroupConfig } from 'components/widgets/ProjectInsights/Toolbar/useSelectedGroupConfig';
import { useAnalytics } from 'hooks/useAnalytics';
import { useFilterUrlSync } from 'hooks/useFilterUrlSync';
import { useResetFiltersOnMapLoad, useMapLocationFromUrl } from 'hooks/useProjectMap';
import { useToast } from 'hooks/useToast';
import { groupAtom, useUserStateSelection } from 'state/dashboard';
import { useProjectInsightsViewType } from 'state/map';
import { ProjectMapPageViewSections } from 'utils/analytics/constants';
import { STATE_CODE_TO_LABEL } from 'utils/constants/location';
import { QUERY_PARAM } from 'utils/constants/QueryParameters';
import { Z_INDEX_DICT } from 'utils/constants/zIndex';
import {
  applyProjectFilterUrlState,
  buildProjectFilterUrlState,
  ProjectFilterUrlState,
} from 'utils/filters/projectFilterUrlState';

const sidebarStyle = {
  overflow: 'hidden',
};

const mainContentStyle = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  zIndex: Z_INDEX_DICT.MAP_MAIN_CONTENT,
};

export const ProjectSearch = () => {
  const { closeAllToasts } = useToast();
  const [selectedState] = useUserStateSelection();
  const [viewType] = useProjectInsightsViewType();
  const { usePageViewTracking } = useAnalytics();
  const [group, setGroup] = useAtom(groupAtom);
  const selectedGroup = useSelectedGroupConfig();
  const sidebarWidth = isChatCreatedSavedSearch(selectedGroup)
    ? CHAT_SIDEBAR_WIDTH
    : DEFAULT_SIDEBAR_WIDTH;

  usePageViewTracking({
    sectionName: ProjectMapPageViewSections.PROJECT_MAP,
    subSectionName: ProjectMapPageViewSections.PROJECT_MAP,
  });
  useMapLocationFromUrl();

  useResetFiltersOnMapLoad();

  // Toolbar filters <-> URL sync (?f=<compressed>). Encode the active filters
  // (everything on the group atom except location, which is synced separately
  // via region-code/state-code) into the URL and, on mount, restore them from a
  // shared/refreshed link. Mounted here at the page level rather than in the
  // toolbar, which re-renders unnecessarily and would clobber the hydration.
  const filterUrlState = useMemo<ProjectFilterUrlState>(
    () => buildProjectFilterUrlState(group),
    [group],
  );
  const applyFilterUrlState = useCallback(
    (state: ProjectFilterUrlState) => {
      // Single atomic merge: per-slice setters would fire multiple renders and
      // the keyword setters append/dedup rather than assign. Omitted keys keep
      // their DEFAULT_GROUP values via ...prev. The Latest Project Stage
      // checkbox tree self-rebuilds from the hydrated `triggers`.
      setGroup((prev) => ({ ...prev, ...applyProjectFilterUrlState(state) }));
    },
    [setGroup],
  );

  useFilterUrlSync({
    paramKey: QUERY_PARAM.filters,
    state: filterUrlState,
    apply: applyFilterUrlState,
    debounceMs: 300,
  });

  useEffect(() => {
    // Cleanup to close all toasts on unmount of the map
    return () => {
      closeAllToasts();
    };
  }, []);

  const pageTitle = useMemo(
    () =>
      selectedState
        ? `Project Map - ${STATE_CODE_TO_LABEL[selectedState]}`
        : 'Project Map',
    [selectedState],
  );

  if (!selectedState) {
    return <GlobalLoader />;
  }

  return (
    <BasePageWithTopNav>
      <PageMetadata title={pageTitle} />
      <SidebarLayout
        hideSidebarOnMobile
        sidebarZIndex={Z_INDEX_DICT.MAP_SIDEBAR}
        sidebarSx={sidebarStyle}
        sidebarWidth={sidebarWidth}
        sidebar={<ProjectInsightsToolbar />}
      >
        <Box sx={mainContentStyle}>
          <SearchResultsViews viewType={viewType} />
        </Box>
      </SidebarLayout>
    </BasePageWithTopNav>
  );
};
